import { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Button, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";

import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";

import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";

import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";

import {

  addNewGame,

  getGamesData,

  addKnowledgeBaseTopic,

} from "../redux/asyncThunk/userActions";

import { useNavigate } from "react-router-dom";

import { sanitizePlainText } from "../utils/inputValidation";

import { AUTH_FIELD_SX } from "../pages/AuthLayout";

import { CONTENT_TYPES, resolveGameContentType } from "../utils/contentType";

import { userSlice } from "../redux/user/userSlice";

import { markTrainerProjectHint } from "../utils/trainerProjectHint";

import { markKbProjectHint } from "../utils/kbProjectHint";



const { selectProject, closeDialog } = userSlice;



const TYPE_HINTS = {

  [CONTENT_TYPES.scenario]:

    "Интерактивный сценарий с видео и ветвлением по выбору.",

  [CONTENT_TYPES.trainer]:

    "После создания добавляйте вопросы, отмечайте правильные ответы и публикуйте тренажёр.",

  [CONTENT_TYPES.knowledge_base]:

    "Название отобразится в боковой панели. Внутри создайте темы, подтемы и пары «вопрос — ответ».",

};



const KB_THEME_HINT =

  "Будет создана новая тема внутри базы знаний. Внутри неё можно добавить подтемы и вопросы.";



export default function NewGame() {

  const dispatch = useDispatch();

  const navigate = useNavigate();



  const games = useSelector((state) => state.user?.games);

  const user = useSelector((state) => state.user?.data?.user);



  const [textProject, setTextProject] = useState("");

  const [contentType, setContentType] = useState(CONTENT_TYPES.knowledge_base);

  const [creating, setCreating] = useState(false);



  const existingKnowledgeBase = useMemo(

    () =>

      (games ?? []).find(

        (game) => resolveGameContentType(game) === CONTENT_TYPES.knowledge_base,

      ) ?? null,

    [games],

  );



  const hasKnowledgeBase = Boolean(existingKnowledgeBase);



  const checkGamesTitle = useCallback(

    (count) => !games.find((el) => el.title === `Новый проект-${count}`),

    [games],

  );



  useEffect(() => {

    if (contentType !== CONTENT_TYPES.scenario) return;



    if (!games?.length) {

      setTextProject("Новый проект-1");

    } else {

      let count = 1;

      while (!checkGamesTitle(count)) {

        count++;

      }

      setTextProject(`Новый проект-${count}`);

    }

  }, [checkGamesTitle, games, contentType]);



  useEffect(() => {

    if (contentType === CONTENT_TYPES.scenario) return;

    setTextProject("");

  }, [contentType]);



  const isTrainer = contentType === CONTENT_TYPES.trainer;

  const isKnowledgeBase = contentType === CONTENT_TYPES.knowledge_base;

  const isKbTheme = isKnowledgeBase && hasKnowledgeBase;



  const dialogTitle = isTrainer

    ? "Новый тренажёр"

    : isKbTheme

      ? "Новая тема"

      : isKnowledgeBase

        ? "Новая база знаний"

        : "Создайте проект";



  const nameLabel = isTrainer

    ? "Название тренажёра"

    : isKnowledgeBase

      ? hasKnowledgeBase

        ? "Название темы"

        : "Название в боковой панели"

      : "Название проекта";



  const namePlaceholder = isKnowledgeBase

    ? hasKnowledgeBase

      ? "Например: Продажи"

      : "Например: База знаний компании"

    : isTrainer

      ? "Например: продуктовые знания"

      : undefined;



  const createDisabled = !textProject.trim() || creating;



  const handleCreate = async () => {

    const trimmedName = textProject.trim();

    setCreating(true);



    try {

      if (isKbTheme && existingKnowledgeBase) {
        const result = await dispatch(
          addKnowledgeBaseTopic({
            gameId: existingKnowledgeBase._id,
            title: trimmedName,
          }),
        ).unwrap();

        const newTopic = result?.topic;
        if (!newTopic?.id) {
          throw new Error("Не удалось добавить тему");
        }

        dispatch(getGamesData({ id: user.id }));
        markKbProjectHint(existingKnowledgeBase._id);
        dispatch(selectProject(existingKnowledgeBase._id));
        dispatch(closeDialog());
        navigate(
          `/main/${existingKnowledgeBase._id}?type=knowledge_base&topic=${newTopic.id}`,
          {
            replace: true,
          },
        );

        return;
      }



      dispatch(

        addNewGame({

          userId: user.id,

          textProject: trimmedName,

          contentType,

          trainerTheme: isTrainer ? trimmedName : "",

          kbTheme: "",

          onSuccess: (createdId) => {

            dispatch(getGamesData({ id: user.id }));

            if (createdId) {

              if (isTrainer) {

                markTrainerProjectHint(createdId);

              }

              if (isKnowledgeBase) {

                markKbProjectHint(createdId);

              }

              dispatch(selectProject(createdId));

              const typeQuery = isTrainer

                ? "?type=trainer"

                : isKnowledgeBase

                  ? "?type=knowledge_base"

                  : "";

              navigate(`/main/${createdId}${typeQuery}`, { replace: true });

            } else {

              navigate("/main", { replace: true });

            }

          },

        }),

      );

    } finally {

      setCreating(false);

    }

  };



  return (

    <div className="app-dialog__body">

      <div className="app-dialog__title">{dialogTitle}</div>



      <span className="app-dialog__label">Тип проекта</span>

      <ToggleButtonGroup

        exclusive

        fullWidth

        size="small"

        value={contentType}

        onChange={(_, value) => {

          if (value) setContentType(value);

        }}

        className="app-dialog__type-toggle app-dialog__type-toggle--three"

      >

        <ToggleButton value={CONTENT_TYPES.knowledge_base}>

          <MenuBookOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />

          База знаний

        </ToggleButton>

        <ToggleButton value={CONTENT_TYPES.scenario}>

          <MovieCreationOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />

          Сценарий

        </ToggleButton>

        <ToggleButton value={CONTENT_TYPES.trainer}>

          <QuizOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />

          Тренажёр

        </ToggleButton>

      </ToggleButtonGroup>



      <TextField

        label={nameLabel}

        value={textProject}

        size="small"

        fullWidth

        sx={AUTH_FIELD_SX}

        placeholder={namePlaceholder}

        onChange={(ev) =>

          setTextProject(sanitizePlainText(ev.target.value, 120))

        }

      />



      <p className="app-dialog__hint">

        {isKbTheme ? KB_THEME_HINT : TYPE_HINTS[contentType]}

      </p>



      <div className="app-dialog__actions">

        <Button

          variant="contained"

          className="app-btn app-btn--primary"

          disabled={createDisabled}

          onClick={handleCreate}

        >

          {creating ? "Создание…" : "Создать"}

        </Button>

      </div>

    </div>

  );

}

